// Not an import: wasm-pkg.d.ts only declares the generated pkg/ module for dependents' typecheck programs.
// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="./wasm-pkg.d.ts" />
import { Injectable } from '@angular/core';
import type { TextShaperLike } from './glyph-shaper';
import type { MappedFace } from './text-scene-adapter';
import { isWasmTrap } from './wasm-trap';

let configuredWasmUrl: string | undefined;

// Explicit wasm URL for the Angular esbuild builder (import.meta.url resolution of the dynamic
// wasm is unreliable there); unset = wasm-bindgen default.
export function configurePsdRenderWasmUrl(url: string): void {
  configuredWasmUrl = url;
}

// Inline wasm answers synchronously, the worker proxy with a promise; every caller awaits either.
type Answer<T> = T | Promise<T>;

interface SceneRendererLike {
  /** wasm-bindgen destructor; absent on the vitest stub. */
  free?(): void;
  /** Черновое разрешение текстур на время жеста; absent on the vitest stub. */
  set_draft_scaling?(on: boolean): void;
  has_font(key: string): boolean;
  register_font(key: string, bytes: Uint8Array): void;
  /** The instance each font key's mapping row names, for build_scene; absent on the vitest stub and on an older wasm pkg. */
  set_mapped_faces?(facesJson: string): void;
  has_image(key: string): boolean;
  register_image(key: string, width: number, height: number, data: Uint8Array): void;
  has_binary?(key: string): boolean;
  register_binary?(key: string, bytes: Uint8Array): void;
  /** Share an uploaded texture under a second key; absent on the vitest stub and on an older wasm pkg. */
  alias_image?(key: string, oldKey: string): boolean;
  render(sceneJson: string): Answer<Uint8Array>;
  render_camera(
    sceneJson: string,
    originX: number,
    originY: number,
    zoom: number,
    outW: number,
    outH: number,
    bgRgba: number,
  ): Answer<Uint8Array>;
  render_region(sceneJson: string, ox: number, oy: number, outW: number, outH: number): Answer<Uint8Array>;
  /** Style resources the last render drew without; absent on the vitest stub and on an older wasm pkg. */
  missing_resources?(): Answer<string[]>;
  build_scene(modelJson: string): Answer<string>;
  hit_test(sceneJson: string, x: number, y: number): Answer<string | undefined>;
}

// A worker gone without an error event (terminate, OOM kill) never answers again; silence this long while a
// request waits counts as a crash. Generous: one heavy render stays far below it even on a throttled CPU.
export const WORKER_SILENCE_TIMEOUT_MS = 30_000;

/** How many times a session replaces a crashed render worker before it falls back to the main thread: a spread
 *  that exhausts every fresh worker would otherwise loop forever. */
export const MAX_RENDERER_RESTARTS = 3;

/** Request/response channel to psd-render.worker.ts. The worker handles messages in order, so a register posted
 *  before a render lands first and fire-and-forget posts need no reply to stay consistent. */
class RenderWorker {
  private nextId = 0;
  private dead = false;
  private readonly pending = new Map<number, { resolve: (value: unknown) => void; reject: (error: Error) => void }>();
  private watchdog?: ReturnType<typeof setTimeout>;
  /** Style resource keys the worker's renders reported drawing without, since this worker started. */
  readonly missing = new Set<string>();

  /** `onDead` fires once when the worker crashes; its textures are gone with it. */
  constructor(
    private readonly worker: Worker,
    private readonly onDead: () => void,
  ) {
    worker.onmessage = ({
      data,
    }: MessageEvent<{ id: number; value?: unknown; missing?: string[]; error?: string; fatal?: boolean }>): void => {
      for (const key of data.missing ?? []) this.missing.add(key);
      const request = this.pending.get(data.id);
      this.pending.delete(data.id);
      this.rearmWatchdog();
      if (data.error !== undefined) request?.reject(new Error(data.error));
      else request?.resolve(data.value);
      // A trapped instance fails every later call. The trap usually hits a fire-and-forget register_image, so
      // nobody may be waiting on this answer — the worker is replaced regardless.
      if (data.fatal) this.die(`psd-render wasm trapped: ${data.error}`);
    };

    worker.onerror = (event): void => this.die(event.message || 'psd-render worker failed');
  }

  get alive(): boolean {
    return !this.dead;
  }

  call<T>(method: string, args: unknown[], transfer: Transferable[] = []): Promise<T> {
    if (this.dead) return Promise.reject(new Error('psd-render worker is gone'));
    const id = this.nextId++;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (value: unknown) => void, reject });
      this.watchdog ??= this.armWatchdog();
      this.worker.postMessage({ id, method, args }, transfer);
    });
  }

  post(method: string, args: unknown[], transfer?: Transferable[]): void {
    this.call(method, args, transfer).catch(error => console.error(`[psd-render worker] ${method} failed`, error));
  }

  terminate(): void {
    clearTimeout(this.watchdog);
    this.worker.terminate();
  }

  private die(reason: string): void {
    if (this.dead) return;
    this.dead = true;
    clearTimeout(this.watchdog);
    this.worker.terminate();
    for (const request of this.pending.values()) request.reject(new Error(reason));
    this.pending.clear();
    this.onDead();
  }

  // Answers arrive in order, so the silence clock restarts on each one: a long queue of quick answers never trips it.
  private rearmWatchdog(): void {
    clearTimeout(this.watchdog);
    this.watchdog = this.pending.size > 0 ? this.armWatchdog() : undefined;
  }

  private armWatchdog(): ReturnType<typeof setTimeout> {
    return setTimeout(() => this.die('psd-render worker stopped answering'), WORKER_SILENCE_TIMEOUT_MS);
  }
}

/** The session renderer living in the worker. has_font/has_image answer from a main-thread mirror — the wasm
 *  register calls never fail, so the mirror is the worker's registry without a round trip. */
class WorkerSceneRenderer implements SceneRendererLike {
  private readonly fonts = new Set<string>();
  private readonly images = new Set<string>();
  private readonly binaries = new Set<string>();

  constructor(private readonly worker: RenderWorker) {}

  free(): void {
    this.worker.post('free', []);
  }

  set_draft_scaling(on: boolean): void {
    this.worker.post('set_draft_scaling', [on]);
  }

  has_font(key: string): boolean {
    return this.fonts.has(key);
  }

  register_font(key: string, bytes: Uint8Array): void {
    this.fonts.add(key);
    this.worker.post('register_font', [key, bytes]);
  }

  // Posted in order with build_scene, so the worker's renderer holds the set before it builds the scene.
  set_mapped_faces(facesJson: string): void {
    this.worker.post('set_mapped_faces', [facesJson]);
  }

  has_image(key: string): boolean {
    return this.images.has(key);
  }

  has_binary(key: string): boolean {
    return this.binaries.has(key);
  }

  register_binary(key: string, bytes: Uint8Array): void {
    this.binaries.add(key);
    const copy = bytes.slice();
    this.worker.post('register_binary', [key, copy], [copy.buffer]);
  }

  register_image(key: string, width: number, height: number, data: Uint8Array): void {
    this.images.add(key);
    // The controller's decode cache keeps reading `data` (re-home by url), so the worker gets its own copy.
    const copy = data.slice();
    this.worker.post('register_image', [key, width, height, copy], [copy.buffer]);
  }

  alias_image(key: string, oldKey: string): boolean {
    if (!this.images.has(oldKey)) return false;
    this.images.add(key);
    this.worker.post('alias_image', [key, oldKey]);
    return true;
  }

  render(sceneJson: string): Promise<Uint8Array> {
    return this.worker.call('render', [sceneJson]);
  }

  render_camera(...args: [string, number, number, number, number, number, number]): Promise<Uint8Array> {
    return this.worker.call('render_camera', args);
  }

  render_region(...args: [string, number, number, number, number]): Promise<Uint8Array> {
    return this.worker.call('render_region', args);
  }

  // Read off the render answers already received: the render awaited before this call carried its own keys.
  missing_resources(): string[] {
    return [...this.worker.missing];
  }

  build_scene(modelJson: string): Promise<string> {
    return this.worker.call('build_scene', [modelJson]);
  }

  hit_test(sceneJson: string, x: number, y: number): Promise<string | undefined> {
    return this.worker.call('hit_test', [sceneJson, x, y]);
  }
}

// origin/zoom are pre-converted to render_camera space (×renderScale / ×dpr÷renderScale), not book px.
export interface CameraArgs {
  originX: number;
  originY: number;
  zoom: number;
  bgRgba: number;
}

type WasmModule = {
  render_scene: (
    json: string,
    fonts: Record<string, Uint8Array>,
    images: Record<string, { width: number; height: number; data: Uint8Array }>,
  ) => Uint8Array;
  hit_test_scene: (json: string, x: number, y: number) => string | undefined;
  font_parses: (bytes: Uint8Array) => boolean;
  TextShaper: new () => TextShaperLike;
  SceneRenderer: new () => SceneRendererLike;
};

/**
 * Кадры сцены для рендера. `version` — устойчивый ключ содержимого (обычно URL снимка): по нему
 * резидентная текстура переиспользуется при переименовании рамки, поэтому его несут ВСЕ публичные
 * входы, а не только приватная подготовка рендерера.
 */
type SceneImages = Record<string, { width: number; height: number; data: Uint8Array; version?: string }>;

export interface BinaryResourceRef {
  ref: string;
  url?: string;
  data?: string;
  mimeType?: string;
  fileName?: string;
}

async function readBinaryResource(ref: BinaryResourceRef): Promise<Uint8Array> {
  if (ref.url) {
    const response = await fetch(ref.url);
    if (!response.ok) throw new Error(`Binary resource ${ref.ref}: HTTP ${response.status}`);
    return new Uint8Array(await response.arrayBuffer());
  }

  if (!ref.data) throw new Error(`Missing binary resource source: ${ref.ref}`);
  return Uint8Array.from(atob(ref.data), char => char.charCodeAt(0));
}

@Injectable({ providedIn: 'root' })
export class PsdRenderWasmService {
  private readonly binaryResources = new Map<string, { version: string; bytes: Uint8Array }>();
  private readonly binaryLoads = new Map<string, { version: string; promise: Promise<void>; token: object }>();
  private readonly registeredBinaryVersions = new Map<string, string>();
  private wasmPromise?: Promise<WasmModule>;
  // undefined result = no worker (jsdom, no configured wasm URL, failed init): the session renderer runs inline.
  private workerPromise?: Promise<RenderWorker | undefined>;
  // Persistent renderer: fonts/images stay uploaded in wasm across frames so a drag/resize repaint doesn't
  // re-copy tens of MB every call. Keyed by ref — refs are URL-stable (fontRefFor), so a key never aliases a different font.
  private sceneRenderer?: SceneRendererLike;
  // ref → the URL last uploaded for it; a mismatch means the box's photo changed and the texture must be re-registered.
  private readonly registeredImageVersions = new Map<string, string | undefined>();
  // ref → the uploaded texture's natural size; a resident ref is never decoded again, so this is the only
  // place its dimensions survive, and crop/cover geometry needs them.
  private readonly registeredImageSizes = new Map<string, { width: number; height: number }>();
  // URL (image identity) → the ref its texture currently lives under. A layout rebuild renames box ids, so the
  // unchanged photo arrives under a new ref: alias it onto that texture instead of copying the RGBA again and
  // throwing away its scaled/premultiplied caches. Invariant: the value is a ref that holds this URL right now.
  private readonly registeredImageRefByUrl = new Map<string, string>();
  // Держим сами: рендерер рождается лениво, и жест может начаться раньше первого кадра.
  private draftScaling = false;
  private readonly rendererLostListeners = new Set<() => void>();
  private workerRestarts = 0;
  // A main-thread renderer trapped. A replacement would live in the same wasm memory (the module and its glue are
  // one per page), and the usual trap is that memory running out, so the session stops rendering here instead.
  private inlineRendererTrapped = false;
  // Style resource keys already warned about: a lost pattern/mask is reported once per session, not per frame.
  private readonly reportedMissingResources = new Set<string>();

  /** LUT/profile bytes are not images. Retain their source bytes so a lost worker can be restored. */
  async prepareBinaryResources(refs: readonly BinaryResourceRef[]): Promise<void> {
    await Promise.all(
      refs.map(ref => {
        const version = ref.url ? `url:${ref.url}` : ref.data ? `data:${ref.data}` : undefined;
        if (!version) return Promise.reject(new Error(`Missing binary resource source: ${ref.ref}`));
        const prior = this.binaryLoads.get(ref.ref);
        if (prior?.version === version) return prior.promise;
        const token = {};
        const promise = (async () => {
          const bytes = await readBinaryResource(ref);
          if (this.binaryLoads.get(ref.ref)?.token === token) this.binaryResources.set(ref.ref, { version, bytes });
        })().catch(error => {
          if (this.binaryLoads.get(ref.ref)?.token === token) this.binaryLoads.delete(ref.ref);
          throw error;
        });
        this.binaryLoads.set(ref.ref, { version, promise, token });
        return promise;
      }),
    );
  }

  /** `listener` fires when the session renderer died with its textures (worker crash); returns the unsubscribe. */
  onRendererLost(listener: () => void): () => void {
    this.rendererLostListeners.add(listener);
    return () => this.rendererLostListeners.delete(listener);
  }

  protected async loadWasm(): Promise<WasmModule> {
    const mod = await import('../../pkg/psd_render');
    await mod.default(configuredWasmUrl ? { module_or_path: configuredWasmUrl } : undefined);
    return mod as unknown as WasmModule;
  }

  // The session scene render (a full-source downscale can take a second) runs in a worker so the main thread
  // stays free for input; TextShaper, font probes and ephemeral bakes stay on the main-thread wasm.
  private startWorker(): Promise<RenderWorker | undefined> {
    if (typeof Worker === 'undefined' || !configuredWasmUrl) return Promise.resolve(undefined);
    let worker: RenderWorker;
    let ready = false;
    // A crash after init: a fresh worker (fresh wasm memory) takes over, up to MAX_RENDERER_RESTARTS per session,
    // then the main thread. The new registry starts empty, so resident refs read as non-resident and callers decode
    // them again; the in-flight requests of the dead worker reject. The replacement is in place before the
    // listeners run — they repaint right away.
    const onDead = (): void => {
      if (!ready) return; // init failure is handled below
      if (this.workerRestarts < MAX_RENDERER_RESTARTS) {
        this.workerRestarts++;
        console.warn('[psd-render] worker crashed, starting a fresh one');
        this.workerPromise = this.startWorker();
      } else {
        console.warn('[psd-render] worker crashed, rendering on the main thread');
        this.workerPromise = Promise.resolve(undefined);
      }

      if (!(this.sceneRenderer instanceof WorkerSceneRenderer)) return;
      this.forgetRenderer();
      for (const listener of this.rendererLostListeners) listener();
    };

    try {
      worker = new RenderWorker(new Worker(new URL('./psd-render.worker.ts', import.meta.url), { type: 'module' }), onDead);
    } catch (error) {
      console.warn('[psd-render] worker unavailable, rendering on the main thread', error);
      return Promise.resolve(undefined);
    }

    return worker.call('init', [new URL(configuredWasmUrl, location.href).href]).then(
      () => {
        ready = true;
        return worker;
      },
      error => {
        console.warn('[psd-render] worker init failed, rendering on the main thread', error);
        worker.terminate();
        return undefined;
      },
    );
  }

  /** Start fetching/instantiating the wasm ahead of first render use. Call configurePsdRenderWasmUrl first. */
  preload(): void {
    this.workerPromise ??= this.startWorker();
    if (this.wasmPromise) return;
    const loading = this.loadWasm();
    this.wasmPromise = loading;
    // A failed preload must not poison the memo — the lazy path retries on next use.
    void loading.catch(() => {
      if (this.wasmPromise === loading) this.wasmPromise = undefined;
    });
  }

  async renderSceneToCanvas(
    canvas: HTMLCanvasElement,
    sceneJson: string,
    fonts: Record<string, Uint8Array>,
    images: SceneImages,
  ): Promise<void> {
    const { width, height } = canvas;
    // The canvas owns the frame size: build_scene rounds the scaled viewport, spreadBufferSize floors it, so
    // `render` (scene viewport) could return one row more than the canvas holds. Same pixels, fixed size.
    await this.useRenderer(fonts, images, async renderer =>
      this.blit(canvas, await renderer.render_region(sceneJson, 0, 0, width, height), width, height),
    );
  }

  async renderCameraToCanvas(
    canvas: HTMLCanvasElement,
    sceneJson: string,
    fonts: Record<string, Uint8Array>,
    images: SceneImages,
    cam: CameraArgs,
    options?: { fullResolution?: boolean },
  ): Promise<void> {
    const { width, height } = canvas;
    await this.useRenderer(fonts, images, async renderer => {
      // Кадр, который переживёт жест (уходит на сервер превью разворота), всегда рисуется в полном
      // разрешении: выключение, render_camera и включение уходят одним синхронным блоком (в воркер — тремя
      // сообщениями подряд, он исполняет их по порядку), между ними ничего не вклинится, и черновой
      // режим возвращается до ожидания пикселей — жест не теряет ни кадра. Восстанавливаем из локального
      // флага, а не через setDraftScaling: тот выходит на неизменённом поле и рассинхронил бы рендерер.
      const liftDraft = options?.fullResolution === true && this.draftScaling;
      if (liftDraft) renderer.set_draft_scaling?.(false);
      let bytes: Answer<Uint8Array>;
      try {
        bytes = renderer.render_camera(sceneJson, cam.originX, cam.originY, cam.zoom, width, height, cam.bgRgba);
      } catch (error) {
        // A trapped renderer rejects the restore as well; the render's own error is the one to report.
        if (liftDraft) {
          try {
            renderer.set_draft_scaling?.(true);
          } catch {
            // poisoned instance — dropped by the caller
          }
        }

        throw error;
      }

      if (liftDraft) renderer.set_draft_scaling?.(true);

      await this.blit(canvas, await bytes, width, height);
    });
  }

  /** One-shot render on a scratch renderer freed right after — the persistent renderer caches textures
   *  by key with no eviction, so routing bulk background renders through it would grow wasm memory unbounded. */
  async renderCameraToCanvasEphemeral(
    canvas: HTMLCanvasElement,
    sceneJson: string,
    fonts: Record<string, Uint8Array>,
    images: SceneImages,
    cam: CameraArgs,
    binaryRefs: readonly BinaryResourceRef[] = [],
  ): Promise<void> {
    this.wasmPromise ??= this.loadWasm();
    const wasm = await this.wasmPromise;
    const renderer = new wasm.SceneRenderer();
    try {
      for (const key in fonts) renderer.register_font(key, fonts[key]);
      for (const key in images) {
        const img = images[key];
        renderer.register_image(key, img.width, img.height, img.data);
      }

      // Bulk previews must not retain each document's LUT/profile in the session cache.
      for (const ref of binaryRefs) {
        if (!renderer.register_binary) throw new Error('The render engine does not support binary adjustment resources');
        renderer.register_binary(ref.ref, await readBinaryResource(ref));
      }

      const { width, height } = canvas;
      const bytes = renderer.render_camera(sceneJson, cam.originX, cam.originY, cam.zoom, width, height, cam.bgRgba);
      await this.blit(canvas, await bytes, width, height);
    } finally {
      renderer.free?.();
    }
  }

  // Region buffer in scene px: ox/oy (≥ 0) shift content right/down so the canvas captures left/top overflow.
  async renderRegionToCanvas(
    canvas: HTMLCanvasElement,
    sceneJson: string,
    fonts: Record<string, Uint8Array>,
    images: SceneImages,
    ox: number,
    oy: number,
  ): Promise<void> {
    const { width, height } = canvas;
    await this.useRenderer(fonts, images, async renderer =>
      this.blit(canvas, await renderer.render_region(sceneJson, ox, oy, width, height), width, height),
    );
  }

  /**
   * Region pixels straight from the engine, for callers that post-process them instead of painting
   * them — the compound-clip re-bake keeps only the alpha of the rendered text.
   */
  async renderRegionToBytes(
    sceneJson: string,
    fonts: Record<string, Uint8Array>,
    images: SceneImages,
    width: number,
    height: number,
    ox = 0,
    oy = 0,
  ): Promise<Uint8Array> {
    return this.useRenderer(fonts, images, renderer => renderer.render_region(sceneJson, ox, oy, width, height));
  }

  /** True when the persistent renderer already holds this ref's texture for the same source URL —
   *  the caller can skip decoding entirely; prepareRenderer would drop the fresh bytes anyway. */
  hasResidentImage(ref: string, version: string | undefined): boolean {
    return !!this.sceneRenderer?.has_image(ref) && this.registeredImageVersions.get(ref) === version;
  }

  /** Natural size of a resident texture — undefined when the ref must be decoded, so callers fall back to a real decode. */
  residentImageSize(ref: string, version: string | undefined): { width: number; height: number } | undefined {
    return this.hasResidentImage(ref, version) ? this.registeredImageSizes.get(ref) : undefined;
  }

  /** Черновое разрешение текстур: в жесте фото садятся на ступень размеров, и соседние тики попадают в кэш
   *  вместо пересемплинга под каждый новый размер рамки. Переключать по ГРАНИЦАМ жеста — первый кадр после
   *  выключения пересемплит под точный размер, и на каждый тик это стоило бы дороже самого жеста. */
  setDraftScaling(on: boolean): void {
    if (this.draftScaling === on) return;
    this.draftScaling = on;
    this.sceneRenderer?.set_draft_scaling?.(on);
  }

  /** Free the session renderer + its uploaded textures. The persistent renderer never evicts, so a consumer that
   *  opens many packages in one session must call this at a package boundary or its wasm texture memory grows unbounded. */
  resetPersistentRenderer(): void {
    this.sceneRenderer?.free?.();
    this.forgetRenderer();
    this.binaryResources.clear();
    this.binaryLoads.clear();
  }

  private forgetRenderer(): void {
    this.sceneRenderer = undefined;
    this.registeredImageVersions.clear();
    this.registeredBinaryVersions.clear();
    this.registeredImageSizes.clear();
    this.registeredImageRefByUrl.clear();
  }

  // The persistent renderer keeps fonts/images uploaded in wasm across frames; only new refs are copied.
  private async prepareRenderer(fonts: Record<string, Uint8Array>, images: SceneImages): Promise<SceneRendererLike> {
    this.wasmPromise ??= this.loadWasm();
    this.workerPromise ??= this.startWorker();
    const [wasm, awaited] = await Promise.all([this.wasmPromise, this.workerPromise]);
    // The worker may have crashed while this call awaited it; its replacement, if any, is already on the way.
    const worker = awaited?.alive === false ? await this.workerPromise : awaited;
    if (!this.sceneRenderer) {
      if (worker?.alive) {
        this.sceneRenderer = new WorkerSceneRenderer(worker);
      } else {
        // One honest error instead of a repaint loop on a renderer that would trap again.
        if (this.inlineRendererTrapped) throw new Error('psd-render: the renderer crashed, reload the page');

        this.sceneRenderer = new wasm.SceneRenderer();
      }

      // Рендерер мог родиться уже внутри жеста — флаг живёт на сервисе, а не на конкретном рендерере.
      if (this.draftScaling) this.sceneRenderer.set_draft_scaling?.(true);
    }

    const renderer = this.sceneRenderer;
    // The first trap usually lands here, in register_image — not in the render that follows.
    this.guardTrap(renderer, () => this.uploadAssets(renderer, fonts, images));
    return renderer;
  }

  private uploadAssets(renderer: SceneRendererLike, fonts: Record<string, Uint8Array>, images: SceneImages): void {
    this.uploadBinaryResources(renderer, this.registeredBinaryVersions);
    for (const key in fonts) if (!renderer.has_font(key)) renderer.register_font(key, fonts[key]);
    for (const key in images) {
      const img = images[key];
      // Re-upload when a replaced photo gives the same box.id a new URL — the renderer caches textures by key forever.
      if (renderer.has_image(key) && this.registeredImageVersions.get(key) === img.version) continue;
      // A renamed box holding the same photo: point the new ref at the resident texture — no copy, and its
      // scaled/premultiplied caches survive (a re-upload evicts them and re-runs the whole downscale).
      // Only a ref without a texture of its own: a resident ref taking another photo is a replacement and re-uploads.
      const twin = img.version !== undefined ? this.registeredImageRefByUrl.get(img.version) : undefined;
      // hasResidentImage re-checks the twin: its own photo may have been replaced since, which makes the map entry stale.
      if (
        twin !== undefined &&
        twin !== key &&
        !renderer.has_image(key) &&
        this.hasResidentImage(twin, img.version) &&
        renderer.alias_image?.(key, twin) === true
      ) {
        this.registeredImageVersions.set(key, img.version);
        this.registeredImageSizes.set(key, this.registeredImageSizes.get(twin) ?? { width: img.width, height: img.height });
        continue;
      }

      if (img.data.length === 0) continue; // a resident placeholder carries no pixels — uploading it would blank the texture
      const previousVersion = this.registeredImageVersions.get(key);
      renderer.register_image(key, img.width, img.height, img.data);
      this.registeredImageVersions.set(key, img.version);
      this.registeredImageSizes.set(key, { width: img.width, height: img.height });
      if (img.version !== undefined) this.registeredImageRefByUrl.set(img.version, key);
      if (previousVersion !== undefined && previousVersion !== img.version) this.releaseUrlHolder(renderer, key, previousVersion);
    }
  }

  private uploadBinaryResources(renderer: SceneRendererLike, versions?: Map<string, string>): void {
    for (const [key, resource] of this.binaryResources) {
      if (renderer.has_binary?.(key) && versions?.get(key) === resource.version) continue;
      if (!renderer.register_binary) throw new Error('The render engine does not support binary adjustment resources');
      renderer.register_binary(key, resource.bytes);
      versions?.set(key, resource.version);
    }
  }

  /** Prepare the session renderer and run `use` on it; a wasm trap in either step drops a main-thread renderer. */
  private async useRenderer<T>(
    fonts: Record<string, Uint8Array>,
    images: SceneImages,
    use: (renderer: SceneRendererLike) => Answer<T>,
  ): Promise<T> {
    const renderer = await this.prepareRenderer(fonts, images);
    try {
      const result = await use(renderer);
      void this.reportMissingResources(renderer);
      return result;
    } catch (error) {
      this.dropTrappedRenderer(renderer, error);
      throw error;
    }
  }

  // The engine drew those effects without their resource (404/410) instead of failing the spread.
  private async reportMissingResources(renderer: SceneRendererLike): Promise<void> {
    try {
      const fresh = ((await renderer.missing_resources?.()) ?? []).filter(key => !this.reportedMissingResources.has(key));
      if (fresh.length === 0) return;
      // RACE-SAFE: a log-dedupe set; two racing reports warn twice at worst.
      for (const key of fresh) this.reportedMissingResources.add(key);
      console.warn('[psd-render] style resources unavailable, effects drawn without them', fresh);
    } catch {
      // A lost worker answers the render's own caller; the warning is best-effort.
    }
  }

  private guardTrap<T>(renderer: SceneRendererLike, run: () => T): T {
    try {
      return run();
    } catch (error) {
      this.dropTrappedRenderer(renderer, error);
      throw error;
    }
  }

  // A trapped wasm renderer fails every later call, so it is freed and forgotten, and every later render answers one
  // honest error (see inlineRendererTrapped). Listeners are not told: a repaint would only meet that error. Only the
  // main-thread renderer: a worker trap arrives as `fatal` and replaces the whole worker, and by the time its caller
  // sees the error the renderer is already gone.
  private dropTrappedRenderer(renderer: SceneRendererLike, error: unknown): void {
    if (renderer instanceof WorkerSceneRenderer || renderer !== this.sceneRenderer || !isWasmTrap(error)) return;
    this.inlineRendererTrapped = true;
    try {
      renderer.free?.();
    } catch {
      // The poisoned borrow rejects free() as well; the instance is abandoned either way.
    }

    this.forgetRenderer();
    console.warn('[psd-render] renderer trapped, rendering stops until the page reloads', error);
  }

  // The ref just dropped this URL; an alias of it may still hold the old texture, so the next ref with that
  // URL must find the alias, not a stale entry that forces a second copy.
  private releaseUrlHolder(renderer: SceneRendererLike, key: string, url: string): void {
    if (this.registeredImageRefByUrl.get(url) !== key) return;
    for (const [ref, version] of this.registeredImageVersions) {
      if (ref !== key && version === url && renderer.has_image(ref)) {
        this.registeredImageRefByUrl.set(url, ref);
        return;
      }
    }

    this.registeredImageRefByUrl.delete(url);
  }

  // width/height = the canvas size the frame was requested for. A newer render resized the canvas while the worker
  // rendered this one: the frame is superseded and its bytes no longer fit — drop it silently.
  private async blit(canvas: HTMLCanvasElement, bytes: Uint8Array, width: number, height: number): Promise<void> {
    if (canvas.width !== width || canvas.height !== height) return;
    // Plain synchronous context: `desynchronized` presented this canvas black on some GPU stacks (Windows/AMD).
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2d context unavailable');
    const imageData = new ImageData(new Uint8ClampedArray(bytes), width, height);
    // A GPU-decoded ImageBitmap + drawImage keeps the canvas on the accelerated path; a bare putImageData
    // forces Chromium into CPU-canvas mode (~7× slower). Fall back where createImageBitmap is absent (jsdom).
    if (typeof createImageBitmap === 'function') {
      const bitmap = await createImageBitmap(imageData);
      // 'copy' replaces the whole buffer in ONE op: a clearRect+drawImage pair leaves a transparent
      // buffer state between them, and the compositor is free to present that gap —
      // it reads as a white flash of the paper on every in-place re-render.
      ctx.globalCompositeOperation = 'copy';
      ctx.drawImage(bitmap, 0, 0);
      ctx.globalCompositeOperation = 'source-over';
      bitmap.close();
    } else {
      ctx.putImageData(imageData, 0, 0);
    }
  }

  /** A fresh rustybuzz-backed font registry; register fonts then shape/measure. */
  async createTextShaper(): Promise<TextShaperLike> {
    this.wasmPromise ??= this.loadWasm();
    const wasm = await this.wasmPromise;
    return new wasm.TextShaper();
  }

  /** Whether the engine can parse these font bytes. A probe that cannot run answers true: the
   *  fallback path exists for fonts that are actually broken, not for a wasm that failed to load. */
  async canEngineParseFont(bytes: Uint8Array): Promise<boolean> {
    try {
      this.wasmPromise ??= this.loadWasm();
      const wasm = await this.wasmPromise;
      return wasm.font_parses(bytes);
    } catch {
      return true;
    }
  }

  /** Build sceneJson from a RenderBox-mirror envelope via the Rust adapter (screen==print path). Fonts must be
   *  registered before the call so text glyphs lay out (build_scene shapes from the renderer's font registry).
   *  `mappedFaces` names, by font key, the instance a mapping row pins — the whole set, as print holds it. */
  async buildScene(
    modelJson: string,
    fonts: Record<string, Uint8Array> = {},
    mappedFaces: Record<string, MappedFace> = {},
  ): Promise<string> {
    return this.useRenderer(fonts, {}, renderer => {
      renderer.set_mapped_faces?.(JSON.stringify(mappedFaces));
      return renderer.build_scene(modelJson);
    });
  }

  /** Topmost interactive drawable id at (x, y) in scene coordinates, or undefined. Pixel-accurate via the
   *  persistent renderer's decoded textures; before the first render (no textures yet) it falls back to the
   *  free pure-geometry test, where Object degrades to its bbox. */
  async hitTest(sceneJson: string, x: number, y: number): Promise<string | undefined> {
    this.wasmPromise ??= this.loadWasm();
    const wasm = await this.wasmPromise;
    const renderer = this.sceneRenderer;
    if (renderer && typeof renderer.hit_test === 'function') {
      return this.guardTrap(renderer, () => renderer.hit_test(sceneJson, x, y));
    }

    return wasm.hit_test_scene(sceneJson, x, y);
  }
}
